import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import InstallBar from "../components/InstallBar";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  can,
  continueListening,
  getBook,
  getBookProgress,
  listChapters,
  type Book,
  type Chapter,
  type Progress,
  type Resume,
  signInURL,
} from "../api";
import BackLink from "../components/BackLink";
import BookWriters from "../components/BookWriters";
import { initialOf, rampFor } from "../components/cover";
import { CheckIcon, PlayIcon } from "../components/icons";
import Placeholder from "../components/Placeholder";
import PlusOffer from "../components/PlusOffer";
import SaveButton from "../components/SaveButton";
import ShareBook from "../components/ShareBook";
import { SeriesNav } from "../components/SeriesLine";
import { BookTags } from "../components/Tags";
import { useT } from "../i18n";
import { primaryOrg, useSession } from "../session";
import ReaderCharacters from "./ReaderCharacters";

/**
 * ReaderBookPage is a book as a listener meets it: the cover, one button that
 * picks up where they are, and the chapters with how long each plays and how
 * far they have got.
 *
 * Its own page rather than the studio's list with the tools hidden. The studio
 * page is a to-do list — what is narrated, what is published — and a reader
 * has no use for a chapter's character count or revision number. What a
 * reader wants to know is what is next and how long it takes.
 */
export default function ReaderBookPage() {
  // The link names the book by a ref (ADR-0210): its readable link, its key,
  // or the id an older link carried. Only the book read takes one; every
  // other call uses the id that read answers.
  const { bookRef = "" } = useParams();
  const { me, isSignedIn } = useSession();
  const org = primaryOrg(me);
  const t = useT();

  // The page's own fetch is the one counted as a page view (ADR-0190 §5), so
  // it is made on every visit, not served from the reader's fetch of the book.
  const book = useQuery({
    queryKey: ["book", bookRef],
    queryFn: () => getBook(bookRef, { screen: "book" }),
    refetchOnMount: "always",
  });
  // An id in the link starts the other reads at once; a key waits for the
  // book read to name the id.
  const bookID = book.data?.id ?? (isUUID(bookRef) ? bookRef : "");
  useCanonicalAddress(book.data?.url);
  const chapters = useQuery({
    queryKey: ["chapters", bookID, "reader"],
    queryFn: () => listChapters(bookID),
    enabled: !!bookID,
  });
  // Positions come from the resume list, newest first. It holds the reader's
  // most recent chapters across every book, which is all of a book being read.
  const resume = useQuery({
    queryKey: ["continue"],
    queryFn: continueListening,
    enabled: isSignedIn,
    retry: false,
  });

  // Each chapter's own progress, for its tick or ring (ADR-0187). Not the
  // resume list: that is the 20 newest rows across every book, and the
  // letters read before them lost their ticks.
  const inBook = useQuery({
    queryKey: ["book-progress", bookID],
    queryFn: () => getBookProgress(bookID),
    enabled: isSignedIn && !!bookID,
    retry: false,
  });

  const items = chapters.data?.items ?? [];
  const mine = (resume.data?.items ?? []).filter((r) => items.some((c) => c.id === r.chapter_id));
  const progress = new Map((inBook.data?.items ?? []).map((p) => [p.chapter_id, p]));
  const next = nextUp(items, mine);
  const totalMs = items.reduce((sum, c) => sum + (c.duration_ms ?? 0), 0);
  const writable = can(org, "chapter.write") && book.data?.org_id === org?.id;
  // A book this viewer may see but not read: its face, and what would open it
  // where the Listen button would be — Get Plus, or signing in (ADR-0193 §4,
  // ADR-0197). The server says which; a visitor signs in first either way,
  // since Plus belongs to an account.
  const access = book.data?.access;
  const locked = !!access && access !== "open";
  const opener =
    access === "plus" && isSignedIn ? `/plus?book=${bookID}` : signInURL(`/books/${bookRef}`);

  return (
    <main className="pb-12">
      {/* On a phone opened from a link: the app, above the book (ADR-0209). */}
      <InstallBar className="mx-4 mt-3" />
      <Hero
        book={book.data}
        meta={[
          items.length > 0 ? t.book.summaryChapters(items.length) : "",
          totalMs > 0 ? t.book.length(minutesOf(totalMs)) : "",
        ]
          .filter(Boolean)
          .join(t.book.sep)}
        top={
          <div className="flex items-center justify-between gap-3">
            {/* Back to wherever this book actually is for this reader
                (ADR-0060). A link that always said "Library" would, since the
                split, send someone who found the book in the catalogue to a
                shelf that does not hold it.

                Rendered with the book, not before it: the label is a fact
                about the book, so guessing while it loads shows a wrong
                answer and then corrects it. The empty span holds the slot —
                `justify-between` keeps "Open in studio" at the end either
                way, so nothing moves when the real link arrives. */}
            {!book.data ? (
              <span />
            ) : book.data.in_library ? (
              <BackLink to="/">{t.book.backToLibrary}</BackLink>
            ) : (
              <BackLink to="/browse">{t.nav.browse}</BackLink>
            )}
            {writable && (
              <Link
                to={`/studio/books/${bookID}`}
                data-testid="open-book-in-studio"
                className="rounded-full bg-surface/70 px-3 py-1.5 text-xs text-ink-soft backdrop-blur transition-colors hover:bg-surface"
              >
                {t.book.openInStudio}
              </Link>
            )}
          </div>
        }
      >
        <div className="flex flex-wrap items-center justify-center gap-3">
          {access === "plus" ? (
            <PlusOffer bookID={bookID} book={book.data} signInFirst={!isSignedIn} />
          ) : access === "sign_in" ? (
            <a
              href={opener}
              data-testid="sign-in-to-read"
              className="inline-flex rounded-full bg-sage px-5 py-2 text-[0.95rem] font-semibold text-on-sage"
            >
              {t.plus.signInToRead}
            </a>
          ) : (
            next && <ListenButton next={next} />
          )}
          {/* Beside the one button, not instead of it: keeping a book is a
              small decision and listening is the big one (ADR-0060). */}
          {book.data && <SaveButton book={book.data} variant="page" />}
          {book.data && <ShareBook book={book.data} />}
        </div>
      </Hero>

      <div className="mx-auto max-w-3xl px-4">
        {book.data?.description && <Description text={book.data.description} />}

        {book.data?.edition && book.data.edition.number > 1 && (
          <div data-testid="book-edition" className="mt-4 text-xs text-muted">
            <p>
              {t.edition.readerLine(
                book.data.edition.number,
                new Date(book.data.edition.published_at).toLocaleDateString(t.meta.dateLocale, {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                }),
              )}
            </p>
            {book.data.edition.note && (
              <p dir="auto" className="mt-0.5 text-ink-soft">
                {book.data.edition.note}
              </p>
            )}
          </div>
        )}

        <section className="mt-8">
          <h2 className="text-lg font-semibold tracking-tight">{t.book.chaptersHeading}</h2>
          {items.length === 0 && !chapters.isPending && (
            <p data-testid="no-chapters" className="mt-2 text-sm text-muted">
              {t.book.noChapters}
            </p>
          )}
          {items.length > 0 && (
            <ol data-testid="chapter-list" className="mt-1 divide-y divide-line">
              {items.map((chapter, i) => (
                <ChapterItem
                  key={chapter.id}
                  chapter={chapter}
                  number={i + 1}
                  at={progress.get(chapter.id)}
                  current={!locked && next?.chapter.id === chapter.id}
                  to={locked ? opener : undefined}
                />
              ))}
            </ol>
          )}
        </section>

        {/* Each opens Browse narrowed to it (0119 T3); below the contents,
            not under the cover (owner, 2026-10-19). */}
        <BookTags tags={book.data?.tags} className="mt-6" />

        {!locked && bookID && <ReaderCharacters bookID={bookID} />}
      </div>
    </main>
  );
}

/**
 * Hero is the top of the page: the cover large, over a wash of its own
 * colours, then the title, author and whatever action the page offers.
 */
function Hero({
  book,
  meta,
  top,
  children,
}: {
  book: Book | undefined;
  meta: string;
  top: React.ReactNode;
  children?: React.ReactNode;
}) {
  const [from, to] = rampFor(book?.id ?? "");
  return (
    <div className="relative isolate overflow-hidden">
      {/* The cover itself, blurred to a colour field: the page takes the
          book's palette without a second image to fetch. */}
      {book?.cover_url ? (
        <img
          src={book.cover_url}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 -z-20 size-full scale-150 object-cover opacity-50 blur-3xl saturate-150"
        />
      ) : (
        book && (
          <div
            aria-hidden="true"
            className="absolute inset-0 -z-20 opacity-30"
            style={{ background: `linear-gradient(160deg, ${from}, ${to})` }}
          />
        )
      )}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 bg-linear-to-b from-ground/10 via-ground/60 to-ground"
      />

      <div className="mx-auto max-w-3xl px-4 pt-4 pb-6">
        {top}
        <div className="mx-auto mt-5 aspect-[3/4] w-44 overflow-hidden rounded-card bg-sunk shadow-[0_24px_48px_-16px_rgb(0_0_0/0.45)] sm:w-52">
          {book?.cover_url ? (
            <img
              src={book.cover_url}
              alt=""
              width={book.cover_width}
              height={book.cover_height}
              data-testid="book-cover"
              className="size-full object-cover"
            />
          ) : book ? (
            <div
              className="grid size-full place-items-center"
              style={{ background: `linear-gradient(150deg, ${from}, ${to})` }}
            >
              <span aria-hidden="true" className="text-6xl font-semibold text-white/85">
                {initialOf(book.title)}
              </span>
            </div>
          ) : (
            <Placeholder className="size-full" />
          )}
        </div>

        <div className="mt-5 text-center">
          {book ? (
            <h1
              dir="auto"
              data-testid="book-title"
              className="text-[1.65rem] leading-tight font-semibold tracking-tight"
            >
              {book.title}
            </h1>
          ) : (
            <Placeholder className="mx-auto h-7 w-3/5 rounded-full" />
          )}
          {book && <BookWriters book={book} className="mt-2" />}
          {book?.series && <SeriesNav series={book.series} />}
          {meta && (
            <p data-testid="book-meta" className="mt-1 text-xs text-muted">
              {meta}
            </p>
          )}
        </div>
        {children && <div className="mt-5">{children}</div>}
      </div>
    </div>
  );
}

/** What the big button does: which chapter, and whether it is a return. */
interface NextUp {
  chapter: Chapter;
  kind: "start" | "continue" | "again" | "read" | "readAgain";
  at?: Resume;
}

/**
 * nextUp decides the page's one action.
 *
 * The chapter last listened to, if it is unfinished; otherwise the next one
 * after it; otherwise, with everything heard, the first again. A reader with
 * no progress starts at the beginning.
 *
 * Only chapters with audio count: a button saying "Start listening" that opens
 * a chapter nobody has narrated is a promise the page cannot keep. A book with
 * no audio at all is still readable, so its button says so instead, and
 * follows the reader's place through the text (ADR-0058).
 */
export function nextUp(items: Chapter[], progress: Resume[]): NextUp | undefined {
  const playable = items.filter(hasAudio);
  const first = playable[0];
  if (!first) return nextToRead(items, progress);
  const latest = progress[0];
  if (!latest) return { chapter: first, kind: "start" };
  const index = items.findIndex((c) => c.id === latest.chapter_id);
  const current = items[index];
  if (!latest.completed && current && hasAudio(current)) {
    return { chapter: current, kind: "continue", at: latest };
  }
  const after = items.slice(index + 1).find(hasAudio);
  return after ? { chapter: after, kind: "continue" } : { chapter: first, kind: "again" };
}

/** nextToRead is nextUp for a book with nothing to play. */
function nextToRead(items: Chapter[], progress: Resume[]): NextUp | undefined {
  const first = items[0];
  const latest = progress[0];
  if (!first) return undefined;
  if (!latest) return { chapter: first, kind: "read" };
  const index = items.findIndex((c) => c.id === latest.chapter_id);
  const current = items[index];
  if (!latest.completed && current) return { chapter: current, kind: "continue", at: latest };
  const after = items[index + 1];
  return after ? { chapter: after, kind: "continue" } : { chapter: first, kind: "readAgain" };
}

/** hasAudio is whether a chapter has anything to play. */
function hasAudio(chapter: Chapter): boolean {
  return (chapter.duration_ms ?? 0) > 0;
}

function ListenButton({ next }: { next: NextUp }) {
  const t = useT();
  const label =
    next.kind === "start"
      ? t.book.startListening
      : next.kind === "read"
        ? t.book.startReading
        : next.kind === "again"
          ? t.book.listenAgain
          : next.kind === "readAgain"
            ? t.book.readAgain
            : t.book.continueListening;
  const left = next.at ? remainingMs(next.chapter, next.at) : 0;
  const percent = next.at ? Math.round(next.at.fraction * 100) : 0;
  return (
    <Link
      to={`/read/${next.chapter.id}`}
      data-testid="listen-button"
      className="group relative flex items-center gap-3 overflow-hidden rounded-full bg-sage py-2 ps-2 pe-5 text-on-sage shadow-[0_10px_24px_-12px_var(--c-sage-deep)] transition-transform active:scale-[0.98]"
    >
      <span className="grid size-10 shrink-0 place-items-center rounded-full bg-on-sage/15">
        <PlayIcon className="size-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[0.95rem] leading-tight font-semibold">{label}</span>
        {/* The title is authored and the rest is the interface's: <bdi>
            keeps a Persian title from reordering "12 min left" around it. */}
        <span className="block truncate text-xs opacity-85">
          <bdi>{next.chapter.title}</bdi>
          {left > 0 && `${t.book.sep}${t.book.minutesLeft(minutesOf(left))}`}
        </span>
      </span>
      {percent > 0 && (
        <span aria-hidden="true" className="absolute inset-x-0 bottom-0 h-1 bg-on-sage/20">
          <span className="block h-full bg-on-sage/70" style={{ width: `${percent}%` }} />
        </span>
      )}
    </Link>
  );
}

function ChapterItem({
  chapter,
  number,
  at,
  current,
  to,
}: {
  chapter: Chapter;
  number: number;
  at: Progress | undefined;
  current: boolean;
  /** Where the row leads instead of the chapter, on a book this viewer may not
   *  read: Get Plus, or the sign-in page (a server page, so a plain link). */
  to?: string;
}) {
  const t = useT();
  const left = at && !at.completed ? remainingMs(chapter, at) : 0;
  // A chapter with nothing to say about it — unread and not narrated — has no
  // second line: "Text only" told the reader nothing to do (owner, 2026-10-19).
  const detail = at?.completed
    ? t.book.finished
    : left > 0
      ? t.book.minutesLeft(minutesOf(left))
      : hasAudio(chapter)
        ? t.book.length(minutesOf(chapter.duration_ms!))
        : null;
  return (
    <li data-testid="chapter-row">
      <RowLink
        to={to ?? `/read/${chapter.id}`}
        className={`-mx-2 flex items-center gap-3.5 rounded-control px-2 py-3 transition-colors hover:bg-sunk ${
          current ? "bg-sage-soft/60" : ""
        }`}
      >
        <Marker number={number} at={at} />
        {/* Aligned by the title, so a Persian chapter's line under it sits
            under it too; the line itself is isolated as the interface's. */}
        <span dir="auto" className="min-w-0 flex-1">
          <span className="block truncate text-[0.95rem] font-medium">{chapter.title}</span>
          {detail && (
            <span data-testid="chapter-detail" className="block text-xs text-muted">
              <bdi>{detail}</bdi>
            </span>
          )}
        </span>
        {current && (
          <span className="grid size-8 shrink-0 place-items-center rounded-full bg-sage text-on-sage">
            <PlayIcon className="size-3.5" />
          </span>
        )}
      </RowLink>
    </li>
  );
}

/** A router link inside the app; a plain one to the server's sign-in page. */
function RowLink({
  to,
  className,
  children,
}: {
  to: string;
  className: string;
  children: React.ReactNode;
}) {
  if (to.startsWith("/api/") || /^https?:/.test(to))
    return (
      <a href={to} className={className}>
        {children}
      </a>
    );
  return (
    <Link to={to} className={className}>
      {children}
    </Link>
  );
}

/**
 * Marker is a chapter's number, a tick once it is finished, or the number in a
 * ring that fills as it is listened to.
 */
function Marker({ number, at }: { number: number; at: Progress | undefined }) {
  const t = useT();
  if (at?.completed) {
    return (
      <span
        data-testid="chapter-finished"
        className="grid size-9 shrink-0 place-items-center rounded-full bg-sage-soft text-sage-deep"
      >
        <CheckIcon className="size-4" />
      </span>
    );
  }
  const fraction = at?.fraction ?? 0;
  const r = 16;
  const circumference = 2 * Math.PI * r;
  return (
    <span className="relative grid size-9 shrink-0 place-items-center rounded-full bg-sunk text-xs font-medium text-ink-soft">
      {fraction > 0 && (
        <svg
          data-testid="chapter-started"
          viewBox="0 0 36 36"
          className="absolute inset-0 size-full -rotate-90"
          aria-hidden="true"
        >
          <circle
            cx="18"
            cy="18"
            r={r}
            fill="none"
            stroke="var(--c-sage)"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeDasharray={`${circumference * fraction} ${circumference}`}
          />
        </svg>
      )}
      {t.meta.num(number)}
    </span>
  );
}

/** Description is the blurb, three lines until the reader asks for the rest. */
function Description({ text }: { text: string }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const long = text.length > 180;
  return (
    <div className="mt-2">
      <p
        dir="auto"
        data-testid="book-description"
        className={`text-[0.95rem] leading-7 text-ink-soft ${open || !long ? "" : "line-clamp-3"}`}
      >
        {text}
      </p>
      {long && (
        <button
          type="button"
          onClick={() => setOpen((was) => !was)}
          className="mt-1 text-sm font-medium text-sage-deep"
        >
          {open ? t.book.less : t.book.more}
        </button>
      )}
    </div>
  );
}

/** minutesOf rounds a duration to whole minutes, never below one. */
export function minutesOf(ms: number): number {
  return Math.max(1, Math.round(ms / 60_000));
}

/**
 * remainingMs is how much of a chapter is still to play. The chapter's own
 * duration when it has one; otherwise what the saved position implies.
 */
export function remainingMs(chapter: Chapter, at: Progress): number {
  const total = chapter.duration_ms || (at.fraction > 0 ? at.position_ms / at.fraction : 0);
  return Math.max(0, total - at.position_ms);
}

const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUUID(s: string): boolean {
  return uuidPattern.test(s);
}

/**
 * useCanonicalAddress puts the book's one link in the address bar once the
 * read has answered (ADR-0210 §4): an old /books/{id} or a link with stale
 * words becomes /books/{words}-{key}, so what a reader copies is the link the
 * Share button gives. The server never redirects — a redirect for a readable
 * book and not for a private one would say which exist. Replaced in place,
 * with the router's own state, so Back does not return to the old form.
 * Run again on every navigation, not only when the link changes: the router
 * knows nothing of this swap, so a later navigation to the old form (Getting
 * Plus navigates to the book twice) would put it back for good.
 */
export function useCanonicalAddress(url: string | undefined) {
  const { key } = useLocation();
  useEffect(() => {
    if (!url) return;
    let path: string;
    try {
      path = new URL(url, window.location.origin).pathname;
    } catch {
      return;
    }
    if (path === window.location.pathname) return;
    window.history.replaceState(
      window.history.state,
      "",
      path + window.location.search + window.location.hash,
    );
  }, [url, key]);
}
